import { Component, Input, OnChanges, OnDestroy, SimpleChanges, signal } from '@angular/core'
import { arrayBufferABlob, type ProveedorPendiente } from '../../infrastructure/offline/db'
import { MiniFotoBlobComponent } from './mini-foto-blob'
import { FotoRemotaComponent } from './foto-remota'
import { VisorImagenComponent } from './visor-imagen'

/**
 * Tarjeta de contacto del proveedor: foto + nombre + contacto (si lo hay).
 * Pedido 2026-09-14 para poder ubicar/contactar a un proveedor sin tener
 * que buscar la foto que se tomo de su tarjeta.
 *
 * Muestra la foto con dos fuentes posibles, en este orden de prioridad:
 * 1. `tarjetaBlob` local (si todavia no se limpio por las 72h de retencion,
 *    ver limpiarBlobsSincronizadosAntiguos en db.ts) -- funciona sin red.
 * 2. `tarjetaUrl` (la URL/key real ya subida, guardada por el sincronizador
 *    tras el primer exito -- ver sincronizador.ts) -- requiere red para
 *    resolverla (FotoRemotaComponent), pero no depende de la antiguedad del
 *    proveedor ni de si el blob local ya se limpio.
 *
 * Si no hay ninguna de las dos, no se muestra nada (mismo criterio
 * defensivo que ya usa el resto de la app en este caso).
 *
 * Reutilizable: usado tanto dentro del modal "Productos de {proveedor}"
 * como en el acceso rapido desde la lista de proveedores (ver inicio.ts),
 * asi la logica de "que foto mostrar" vive en un solo lugar.
 */
@Component({
  selector: 'app-tarjeta-proveedor',
  standalone: true,
  imports: [MiniFotoBlobComponent, FotoRemotaComponent, VisorImagenComponent],
  templateUrl: './tarjeta-proveedor.html',
})
export class TarjetaProveedorComponent implements OnChanges, OnDestroy {
  @Input() proveedor: ProveedorPendiente | null = null

  readonly blobTarjeta = signal<Blob | undefined>(undefined)
  readonly urlTarjeta = signal<string | undefined>(undefined)
  readonly verAmpliada = signal(false)

  // Publico (no privado) a proposito -- el template lo lee directo para
  // pasarselo a VisorImagenComponent; Angular con strictTemplates no deja
  // acceder a miembros `private` desde el HTML.
  urlAmpliadaActual?: string

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['proveedor']) return
    // Si el input cambia (ej. este mismo componente se reutiliza para otro
    // proveedor) mientras el visor ampliado esta abierto, lo cerramos -- sin
    // esto quedaria mostrando la foto del proveedor anterior.
    if (this.verAmpliada()) this.cerrarAmpliada()
    const p = this.proveedor
    const tieneBlob = Boolean(p?.tarjetaBlob && p.tarjetaBlob.byteLength > 0)
    this.blobTarjeta.set(tieneBlob ? arrayBufferABlob(p!.tarjetaBlob!) : undefined)
    this.urlTarjeta.set(tieneBlob ? undefined : p?.tarjetaUrl)
  }

  get hayFoto(): boolean {
    return Boolean(this.blobTarjeta() || this.urlTarjeta())
  }

  /** Para VisorImagenComponent: blob local -> URL temporal; remota -> se pasa tal cual (el visor ya la resuelve). */
  abrirAmpliada(): void {
    if (!this.hayFoto) return
    const blob = this.blobTarjeta()
    this.urlAmpliadaActual = blob ? URL.createObjectURL(blob) : this.urlTarjeta()
    this.verAmpliada.set(true)
  }

  cerrarAmpliada(): void {
    this.verAmpliada.set(false)
    this.liberarUrlAmpliadaSiEsBlob()
  }

  ngOnDestroy(): void {
    this.liberarUrlAmpliadaSiEsBlob()
  }

  /**
   * Revoca la URL temporal solo si de verdad es un `blob:` creado por
   * abrirAmpliada() -- comprobar el prefijo en vez de leer `blobTarjeta()`
   * evita revocar de mas (o de menos) si `proveedor` cambio mientras el
   * visor seguia abierto (ver guarda en ngOnChanges).
   */
  private liberarUrlAmpliadaSiEsBlob(): void {
    if (this.urlAmpliadaActual?.startsWith('blob:')) URL.revokeObjectURL(this.urlAmpliadaActual)
    this.urlAmpliadaActual = undefined
  }
}
